import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Section, Button, Input, Select, Textarea, Badge, Empty } from "@/components/ui/kit";
import { Save, X, Search, Filter, AlertCircle, ChevronDown, Plus } from "lucide-react";

export default function UtilizationAdd() {
  const nav = useNavigate();
  const [purposes, setPurposes] = useState([]);
  const [donations, setDonations] = useState([]);
  
  // Custom Dropdown State for Multiple Sources
  const dropdownRef = useRef(null);
  const [donationSearchTerm, setDonationSearchTerm] = useState("");
  const [isDonationDropdownOpen, setIsDonationDropdownOpen] = useState(false);
  
  // Split Funding Array: [{ donationId, donorName, purposeName, maxAmount, allocatedAmount }]
  const [fundingSources, setFundingSources] = useState([]);

  // Beneficiaries Search State
  const [bens, setBens] = useState([]);
  const [hasSearchedBens, setHasSearchedBens] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [dynamicSchema, setDynamicSchema] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [dynamicBlocks, setDynamicBlocks] = useState([]);
  const [isLoadingBlocks, setIsLoadingBlocks] = useState(false);
  const [selectedBens, setSelectedBens] = useState([]); 
  const [busy, setBusy] = useState(false);
  
  const today = new Date().toISOString().slice(0,10);
  const [f, setF] = useState({ 
    sectorId: "", projectId: "", activityId: "",
    utilizationDate: today, utilizedAmount: "", utilizationDescription: "", 
    search: "", benType: "" 
  });

  // Handle clicking outside dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsDonationDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    api.get("/donation-purposes").then(r => setPurposes(r.data.data));
    api.get("/donations", { params: { size: 500 } }).then(r => setDonations(r.data.data.content));
  }, []);

  const sectors = useMemo(() => purposes.filter(p => p.type === "SECTOR"), [purposes]);
  const projects = useMemo(() => purposes.filter(p => p.type === "PROJECT" && (!f.sectorId || String(p.parentId) === String(f.sectorId))), [purposes, f.sectorId]);
  const activities = useMemo(() => purposes.filter(p => p.type === "ACTIVITY" && (!f.projectId || String(p.parentId) === String(f.projectId))), [purposes, f.projectId]);

  // Format options for the SearchableSelect component
  const sectorOptions = useMemo(() => sectors.map(s => ({ id: s.id, name: s.name })), [sectors]);
  const projectOptions = useMemo(() => projects.map(p => ({ id: p.id, name: p.name })), [projects]);
  const activityOptions = useMemo(() => activities.map(a => ({ id: a.id, name: a.name })), [activities]);

  const activePurposeId = f.activityId || f.projectId || f.sectorId;
  
  const allowedPurposeNames = useMemo(() => {
    if (!activePurposeId) return [];
    const allowed = [];
    let current = purposes.find(p => String(p.id) === String(activePurposeId));
    while (current) {
      allowed.push(current.name);
      current = purposes.find(p => String(p.id) === String(current.parentId));
    }
    return allowed;
  }, [activePurposeId, purposes]);

  const upd = (k) => (e) => setF(v => ({ ...v, [k]: e.target.value }));

  // Filter Donations based on search & hierarchy
  const filteredDonations = useMemo(() => {
    let filtered = donations;
    if (activePurposeId) {
      filtered = filtered.filter(d => !d.purposeName || d.purposeName === "ANY" || allowedPurposeNames.includes(d.purposeName));
    }
    if (donationSearchTerm) {
      const term = donationSearchTerm.toLowerCase();
      filtered = filtered.filter(d => d.donorName?.toLowerCase().includes(term) || d.purposeName?.toLowerCase().includes(term) || String(d.donationId).includes(term));
    }
    const addedIds = fundingSources.map(s => s.donationId);
    return filtered.filter(d => !addedIds.includes(d.donationId));
  }, [donations, activePurposeId, allowedPurposeNames, donationSearchTerm, fundingSources]);

  // Add a donation to the split list
  const addFundingSource = async (d) => {
    try {
      const res = await api.get(`/donations/${d.donationId}/journey`);
      const balance = res.data.data.remainingBalance;
      
      if (balance <= 0) {
        toast.error("This donation has zero remaining balance.");
        return;
      }
      
      setFundingSources(prev => [...prev, {
        donationId: d.donationId,
        donorName: d.donorName,
        purposeName: d.purposeName,
        maxAmount: balance, 
        allocatedAmount: ""
      }]);
      setDonationSearchTerm("");
      setIsDonationDropdownOpen(false);
    } catch (err) {
      toast.error("Failed to load donation balance");
    }
  };

  const updateSourceAmount = (id, amount) => {
    setFundingSources(prev => prev.map(s => s.donationId === id ? { ...s, allocatedAmount: amount } : s));
  };

  const removeSource = (id) => {
    setFundingSources(prev => prev.filter(s => s.donationId !== id));
  };

  const totalAllocated = useMemo(() => {
    return fundingSources.reduce((sum, s) => sum + (Number(s.allocatedAmount) || 0), 0);
  }, [fundingSources]);

  // Beneficiary logic
  useEffect(() => {
    setBens([]); setHasSearchedBens(false); setDynamicBlocks([]);
    if (f.benType === "STUDENT") {
      setIsLoadingFilters(true);
      api.get("/beneficiaries/filters-schema", { params: { type: "STUDENT" } })
        .then(r => { setDynamicSchema((r.data.data.filters || []).filter(flt => flt.field !== "block")); setFilterValues({}); })
        .finally(() => setIsLoadingFilters(false));
    } else { setDynamicSchema([]); setFilterValues({}); }
  }, [f.benType]);

  const handleDynamicFilterChange = (field, value) => {
    setFilterValues(prev => ({ ...prev, [field]: value }));
    if (field === "district") {
      setFilterValues(prev => ({ ...prev, block: "" })); setDynamicBlocks([]);
      if (value) {
        setIsLoadingBlocks(true);
        api.get("/beneficiaries/blocks", { params: { district: value } })
          .then(r => setDynamicBlocks(r.data.data || [])).finally(() => setIsLoadingBlocks(false));
      }
    }
  };

  const handleSearchBeneficiaries = async () => {
    if (!f.benType) return toast.error("Please select a beneficiary type first");
    setIsSearching(true); setHasSearchedBens(true);
    try {
      const r = await api.get("/beneficiaries", { params: { size: 200, type: f.benType, search: f.search || undefined, ...filterValues } });
      setBens(r.data.data.content || []);
    } catch (err) { toast.error("Failed to fetch beneficiaries"); setBens([]); } finally { setIsSearching(false); }
  };

  const toggle = (b) => {
    setSelectedBens(prev => prev.find(x => x.beneficiaryId === b.beneficiaryId) ? prev.filter(x => x.beneficiaryId !== b.beneficiaryId) : [...prev, b]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!activePurposeId) return toast.error("Please select a sector / project / activity");
    if (!f.utilizedAmount) return toast.error("Enter a total utilization amount");

    if (totalAllocated > Number(f.utilizedAmount)) {
      return toast.error(`Mapped donation amounts (₹${totalAllocated}) exceed total event amount (₹${f.utilizedAmount})`);
    }

    for (let s of fundingSources) {
      if (!s.allocatedAmount || Number(s.allocatedAmount) <= 0) {
        return toast.error(`Enter a valid amount for Donation #${s.donationId}`);
      }
      if (Number(s.allocatedAmount) > s.maxAmount) {
        return toast.error(`Donation #${s.donationId} only has ₹${s.maxAmount} available.`);
      }
    }

    setBusy(true);
    try {
      const payload = {
        donationPurposeId: Number(activePurposeId), 
        utilizationDate: f.utilizationDate,
        utilizedAmount: Number(f.utilizedAmount),
        utilizationDescription: f.utilizationDescription,
        fundingSources: fundingSources.map(s => ({ donationId: s.donationId, amount: Number(s.allocatedAmount) })),
        beneficiaryIds: selectedBens.map(b => b.beneficiaryId), 
      };
      
      await api.post("/utilizations", payload);
      toast.success("Utilization recorded successfully");
      nav("/admin/utilizations");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save utilization");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="utilization-add-form">
      <Section title="Record Fund Utilization" kicker="Where the money goes" right={
        <Button type="submit" disabled={busy} data-testid="save-utilization-btn">
          <Save className="w-4 h-4 mr-2"/> Save utilization
        </Button>
      }/>

      <div className="rounded-xl border border-emerald-900/10 bg-white p-6 shadow-sm space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
          
          {/* Searchable Sector */}
          <div className="flex flex-col gap-1" data-testid="fld-sector">
            <label className="text-xs font-medium text-emerald-950">Sector</label>
            <SearchableSelect
              placeholder="— select sector —"
              options={sectorOptions}
              value={f.sectorId}
              onChange={val => setF(v => ({ ...v, sectorId: val, projectId: "", activityId: "" }))}
            />
          </div>

          {/* Searchable Project */}
          <div className="flex flex-col gap-1" data-testid="fld-project">
            <label className="text-xs font-medium text-emerald-950">Project (optional)</label>
            <SearchableSelect
              placeholder="— select project —"
              options={projectOptions}
              value={f.projectId}
              disabled={!f.sectorId && projectOptions.length === 0}
              onChange={val => setF(v => ({ ...v, projectId: val, activityId: "" }))}
            />
          </div>

          {/* Searchable Activity */}
          <div className="flex flex-col gap-1" data-testid="fld-activity">
            <label className="text-xs font-medium text-emerald-950">Activity (optional)</label>
            <SearchableSelect
              placeholder="— select activity —"
              options={activityOptions}
              value={f.activityId}
              disabled={!f.projectId && activityOptions.length === 0}
              onChange={val => setF(v => ({ ...v, activityId: val }))}
            />
          </div>

          <Input label="Date" type="date" testid="fld-date" value={f.utilizationDate} onChange={upd("utilizationDate")} />
          <Input label="Total Amount (INR)" type="number" required min="1" step="0.01" testid="fld-amount" value={f.utilizedAmount} onChange={upd("utilizedAmount")} />
          
          <div className="lg:col-span-1">
            <Textarea label="Description" testid="fld-desc" rows={1} value={f.utilizationDescription} onChange={upd("utilizationDescription")} />
          </div>
        </div>
      </div>

      {/* MULTI-FUNDING SPLIT SECTION */}
      <div className="rounded-xl border border-emerald-900/10 bg-white p-6 shadow-sm space-y-5">
        <div>
          <div className="overline">Split Funding Details</div>
          <div className="font-display text-lg font-bold text-emerald-950">Map donations to this expense</div>
          <div className="text-sm text-muted-foreground">Select one or multiple donations to fund this event.</div>
        </div>

        <div className="relative" ref={dropdownRef}>
          <div className="text-sm font-semibold text-emerald-950 mb-1.5">Add Specific Donation Source</div>
          <div className="relative">
            <Input 
              placeholder="Search donor name, purpose, or ID..." 
              value={donationSearchTerm}
              onChange={(e) => {
                setDonationSearchTerm(e.target.value);
                setIsDonationDropdownOpen(true);
              }}
              onClick={() => setIsDonationDropdownOpen(true)}
            />
            <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-emerald-900/40 pointer-events-none" />
          </div>

          {isDonationDropdownOpen && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-64 overflow-y-auto bg-white border border-emerald-900/10 rounded-lg shadow-xl">
              {filteredDonations.length === 0 ? (
                <div className="px-4 py-4 text-sm text-center text-muted-foreground">No matching donations available</div>
              ) : (
                filteredDonations.map(d => (
                  <button key={d.donationId} type="button" className="w-full text-left px-4 py-2.5 text-sm hover:bg-emerald-50 border-b border-emerald-50 transition-colors" 
                    onClick={() => addFundingSource(d)}>
                    <div className="font-semibold text-emerald-950 flex justify-between">
                      <span>#{d.donationId} • {d.donorName}</span>
                      <span className="text-emerald-700 flex items-center gap-1"><Plus className="w-3 h-3"/> Select</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Donated for: {d.purposeName || "General / ANY"}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Selected Sources List */}
        {fundingSources.length > 0 && (
          <div className="border border-emerald-100 rounded-xl overflow-hidden mt-4">
            <table className="w-full text-sm text-left">
              <thead className="bg-emerald-50/80 text-emerald-900 border-b border-emerald-100">
                <tr>
                  <th className="px-4 py-3 font-semibold">Donation</th>
                  <th className="px-4 py-3 font-semibold">Available Limit (INR)</th>
                  <th className="px-4 py-3 font-semibold w-48">Amount to Utilize (INR)</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-emerald-100">
                {fundingSources.map(s => (
                  <tr key={s.donationId} className="bg-white">
                    <td className="px-4 py-3">
                      <div className="font-medium text-emerald-950">#{s.donationId} • {s.donorName}</div>
                      <div className="text-xs text-muted-foreground">{s.purposeName || "General"}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-emerald-700">₹{s.maxAmount}</td>
                    <td className="px-4 py-3">
                      <Input type="number" min="1" max={s.maxAmount} placeholder="₹ 0"
                        value={s.allocatedAmount} onChange={e => updateSourceAmount(s.donationId, e.target.value)}
                        className="!py-1.5" />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" onClick={() => removeSource(s.donationId)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Dynamic Balance Calculation */}
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 text-blue-900 rounded-lg text-sm font-medium">
          <AlertCircle className="w-5 h-5 text-blue-600 shrink-0" />
          <div className="flex-1">
            Event Total: <span className="font-bold">₹{f.utilizedAmount || 0}</span> • 
            Mapped from Donations: <span className="font-bold">₹{totalAllocated}</span>
          </div>
          {Number(f.utilizedAmount) > 0 && totalAllocated < Number(f.utilizedAmount) && (
            <div className="text-blue-700 bg-blue-100 px-2 py-1 rounded">
              Remaining ₹{Number(f.utilizedAmount) - totalAllocated} will draw from General Pool
            </div>
          )}
        </div>
      </div>

      {/* BENEFICIARY SECTION */}
      <div className="rounded-xl border border-emerald-900/10 bg-white p-6 space-y-4 shadow-sm">
        <div>
          <div className="overline">Beneficiaries</div>
          <div className="font-display text-lg font-bold text-emerald-950">Select who this reached</div>
        </div>
          
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select testid="ben-type-filter" value={f.benType} onChange={e => { setF(v => ({...v, benType: e.target.value})); setBens([]); setHasSearchedBens(false); }}>
            <option value="">Select Type to search…</option>
            <option value="STUDENT">Student</option>
            <option value="SCHOOL">School</option>
            <option value="COMMUNITY">Community</option>
          </Select>
          <Input testid="ben-search" placeholder="Search by name…" value={f.search} onChange={upd("search")} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), handleSearchBeneficiaries())} />
        </div>

        {dynamicSchema.length > 0 && (
          <div className="p-4 bg-emerald-50/50 border border-emerald-100 rounded-lg space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
              <Filter className="w-4 h-4" /> {isLoadingFilters ? "Loading Filters..." : "Filter Hierarchy (Optional Selection)"}
            </div>
            {!isLoadingFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {dynamicSchema.map(filter => filter.inputType === "select" ? (
                  <Select key={filter.field} value={filterValues[filter.field] || ""} onChange={(e) => handleDynamicFilterChange(filter.field, e.target.value)}>
                    <option value="">All {filter.label}s</option>
                    {filter.options?.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </Select>
                ) : (
                  <Input key={filter.field} placeholder={filter.label} type={filter.inputType || "text"} value={filterValues[filter.field] || ""} onChange={(e) => handleDynamicFilterChange(filter.field, e.target.value)} />
                ))}
                <Select value={filterValues["block"] || ""} onChange={(e) => handleDynamicFilterChange("block", e.target.value)} disabled={!filterValues["district"] || isLoadingBlocks}>
                  <option value="">{isLoadingBlocks ? "Loading blocks..." : "All Blocks"}</option>
                  {dynamicBlocks.map(block => <option key={block.value} value={block.value}>{block.label}</option>)}
                </Select>
              </div>
            )}
          </div>
        )}

        {f.benType && (
          <div className="flex justify-end pt-2 border-b border-emerald-900/10 pb-4">
            <Button type="button" onClick={handleSearchBeneficiaries} disabled={isSearching} className="flex items-center gap-2">
              <Search className="w-4 h-4" /> {isSearching ? "Applying Filters..." : "Apply Filters & Fetch Data"}
            </Button>
          </div>
        )}

        {selectedBens.length > 0 && (
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100 flex flex-wrap gap-2">
            <div className="w-full text-xs font-semibold text-emerald-800 mb-1">Attached ({selectedBens.length}):</div>
            {selectedBens.map(sb => (
              <div key={sb.beneficiaryId} className="flex items-center gap-1 bg-white border border-emerald-200 px-2 py-1 rounded-md text-xs font-medium text-emerald-900 shadow-sm">
                <Badge tone={sb.type === "STUDENT" ? "brand" : sb.type === "SCHOOL" ? "indigo" : "warm"}>{sb.type.charAt(0)}</Badge>
                {sb.name}
                <button type="button" onClick={() => setSelectedBens(prev => prev.filter(x => x.beneficiaryId !== sb.beneficiaryId))} className="text-emerald-600 hover:text-red-500 ml-1">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[420px] overflow-y-auto pr-2" style={{ opacity: isSearching ? 0.5 : 1 }}>
          {hasSearchedBens ? (
            bens.length > 0 ? (
              bens.map(b => {
                const on = selectedBens.some(x => x.beneficiaryId === b.beneficiaryId);
                return (
                  <button type="button" key={b.beneficiaryId} onClick={() => toggle(b)} className={`text-left rounded-lg border p-3 transition-colors ${on ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-emerald-900/10 bg-white hover:bg-emerald-50/50"}`}>
                    <div className="flex items-center justify-between">
                      <div className="mono text-[11px] text-emerald-800">{b.code || b.enr_id}</div>
                      <Badge tone={b.type === "STUDENT" ? "brand" : b.type === "SCHOOL" ? "indigo" : "warm"}>{b.type}</Badge>
                    </div>
                    <div className="mt-1 font-semibold text-emerald-950">{b.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{b.address || "—"}</div>
                    {b.medium && <div className="mt-auto pt-2 text-[10px] uppercase tracking-wider text-emerald-700/70 font-semibold">Medium: {b.medium}</div>}
                  </button>
                );
              })
            ) : <div className="col-span-full py-8 text-center"><Empty title="No beneficiaries match your search" /></div>
          ) : <div className="col-span-full py-8 text-center text-sm text-muted-foreground">Select filters above and click Apply Filters to view results.</div>}
        </div>
      </div>
    </form>
  );
}

// --- Custom Searchable Select Component ---
function SearchableSelect({ value, onChange, options, placeholder, disabled }) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (value) {
      const selectedOpt = options.find(o => String(o.id) === String(value));
      if (selectedOpt) setSearch(selectedOpt.name);
    } else {
      setSearch("");
    }
  }, [value, options]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        const selectedOpt = options.find(o => String(o.id) === String(value));
        setSearch(selectedOpt ? selectedOpt.name : "");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, options]);

  const filtered = options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        placeholder={placeholder}
        value={search}
        disabled={disabled}
        onChange={e => {
          setSearch(e.target.value);
          setIsOpen(true);
          if (value) onChange(""); 
        }}
        onClick={() => { if (!disabled) setIsOpen(true); }}
      />
      <ChevronDown className={`absolute right-3 top-3 w-4 h-4 pointer-events-none transition-transform ${isOpen ? "rotate-180" : ""} ${disabled ? "text-emerald-900/20" : "text-emerald-900/40"}`} />

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto bg-white border border-emerald-900/10 rounded-lg shadow-xl">
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-center text-muted-foreground">No matches found</div>
          ) : (
            <>
              {value && (
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-b border-emerald-50 transition-colors"
                  onClick={() => { onChange(""); setSearch(""); setIsOpen(false); }}
                >
                  Clear Selection
                </button>
              )}
              {filtered.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-emerald-50 transition-colors ${String(value) === String(opt.id) ? "bg-emerald-50/50 font-semibold text-emerald-900" : "text-emerald-950"}`}
                  onClick={() => {
                    onChange(opt.id);
                    setSearch(opt.name);
                    setIsOpen(false);
                  }}
                >
                  {opt.name}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}